// src/components/Miniroom/MiniroomEditor.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom"; // 결제 리디렉션 파라미터 처리용
import {
    Box, Stack, Button, Typography, Paper,
    IconButton, Tooltip, Snackbar, Alert, CircularProgress, List, ListItem, ListItemAvatar, Avatar, ListItemText,
    Tabs, Tab, TextField
} from "@mui/material";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ReplayIcon from "@mui/icons-material/Replay";
import api from "../../api/axios";
import { Rnd } from "react-rnd";
import FlipToFrontIcon from "@mui/icons-material/FlipToFront";
import FlipToBackIcon from "@mui/icons-material/FlipToBack";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid"; // 아이콘 아무거나
const GRID = 32;
const ROTATION_STEP = 90;
const HANDLE = 10; // 핸들 크기(px)

export default function MiniroomEditor() {
    const [hasAccess, setHasAccess] = useState(null); // null=로딩중, true/false=권한
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [searchParams] = useSearchParams();
    // 편집 가능 여부에 따라 동작 제한
    const editingDisabled = hasAccess !== true; // 권한 확인 전(null)에도 막기



    const [room, setRoom] = useState(null);
    const [bgId, setBgId] = useState(null);


    const [catalogMode, setCatalogMode] = useState("owned");        // 'owned' | 'shop'
    const [itemTab, setItemTab] = useState("furniture");            // 'furniture' | 'background'


    // 내가 소유 
    const [ownedFurns, setOwnedFurns] = useState([]); // Furniture[] 
    const [ownedBgs, setOwnedBgs] = useState([]);     // Background[] 
    // 상점 
    const [shopFurns, setShopFurns] = useState([]);   // ShopFurniture[] (price 포함) 
    const [shopBgs, setShopBgs] = useState([]);       // ShopBackground[] (price 포함)
    const [placements, setPlacements] = useState([]); // [{id?, furniture, x,y,rotation,z_hint}]
    const [selectedIdx, setSelectedIdx] = useState(null);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

    const ownedFurnIds = useMemo(() => new Set(ownedFurns.map(f => f.id)), [ownedFurns]);
    const ownedBgIds = useMemo(() => new Set(ownedBgs.map(b => b.id)), [ownedBgs]);



    const canvasRef = useRef(null);
    const bg = room?.background;

    // ---------- API ----------
    const fetchEntitlement = useCallback(async () => {
        try {
            const { data } = await api.get("/api/miniroom/entitlement/");
            setHasAccess(!!data?.has_access);
        } catch {
            setHasAccess(false);
        }
    }, []);
    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            await fetchEntitlement(); // 먼저 권한 확인
            const [roomRes, ownedFurnRes, ownedBgRes, shopFurnRes, shopBgRes] = await Promise.all([
                api.get("/api/miniroom/room/"),
                api.get("/api/miniroom/owned/furnitures/"),
                api.get("/api/miniroom/owned/backgrounds/"),
                api.get("/api/miniroom/shop/furnitures/"),
                api.get("/api/miniroom/shop/backgrounds/"),
            ]);
            setRoom(roomRes.data);
            const bg = roomRes.data.background;
            setBgId(bg?.id ?? null);

            setPlacements(roomRes.data.placements || []);
            setOwnedFurns(ownedFurnRes.data || []);
            setOwnedBgs(ownedBgRes.data || []);
            setShopFurns(shopFurnRes.data || []);
            setShopBgs(shopBgRes.data || []);
        } catch (e) {
            console.error(e);
            setToast({ open: true, message: "불러오기에 실패했습니다.", severity: "error" });
        } finally {
            setLoading(false);
        }
    }, [fetchEntitlement]);

    useEffect(() => { loadAll(); }, [loadAll]);

    // 결제 성공 리디렉션 처리 (?purchase=success 같은 파라미터) 
    useEffect(() => {
        if (searchParams.get("purchase") === "success") {
            fetchEntitlement();
            setToast({ open: true, message: "구매가 완료되었습니다. 편집이 활성화됩니다.", severity: "success" });
        }
    }, [searchParams, fetchEntitlement]);

    // Leaf 결제 시작 
    const startPurchase = useCallback(async () => {
        try {
            setPurchaseLoading(true);
            // 1) 백엔드 경유형 예시: 
            // const { data } = await api.post("/api/payments/leaf/checkout", { product_id: "miniroom_pro" }); 
            // window.location.href = data.checkout_url; // 또는 Leaf SDK에 data.payment_payload 전달 

            // 2) 프런트 SDK형 예시 (가짜코드): 
            // await Leaf.openCheckout({ productId: "miniroom_pro", onSuccess: () => { 
            //   fetchEntitlement();  // 성공 시 권한 재조회 
            // }}); 

            // 데모용 토스트(실사용시 위 로직으로 교체) 
            setTimeout(() => {
                setToast({ open: true, message: "결제 성공(데모). 권한 갱신합니다.", severity: "success" });
                fetchEntitlement();
                setPurchaseLoading(false);
            }, 800);
        } catch (e) {
            setPurchaseLoading(false);
            setToast({ open: true, message: "결제 중 오류가 발생했습니다.", severity: "error" });
        }
    }, [fetchEntitlement]);

    const buyFromShop = useCallback(async (type, id) => {
        try {
            // 여기에 Leaf 결제 트리거(프런트SDK/백엔드경유) 넣어도 되고,
            // 결제 완료 후 아래 purchase API를 호출해 소유권 반영을 완료시켜도 OK.
            // 예시: await Leaf.openCheckout(...)

            await api.post("/api/miniroom/shop/purchase/", { type, id });
            setToast({ open: true, message: "구매 완료! 내 소유로 이동했습니다.", severity: "success" });

            // 최신화: 소유/상점 목록 업데이트
            const [ownedFurnRes, ownedBgRes, shopFurnRes, shopBgRes] = await Promise.all([
                api.get("/api/miniroom/owned/furnitures/"),
                api.get("/api/miniroom/owned/backgrounds/"),
                api.get("/api/miniroom/shop/furnitures/"),
                api.get("/api/miniroom/shop/backgrounds/"),
            ]);
            setOwnedFurns(ownedFurnRes.data || []);
            setOwnedBgs(ownedBgRes.data || []);
            setShopFurns(shopFurnRes.data || []);
            setShopBgs(shopBgRes.data || []);

            // 변경:
            if (type === "furniture") setItemTab("furniture");
            else setItemTab("background");
            setCatalogMode("owned");
        } catch (e) {
            const msg = e?.response?.data?.detail || "구매에 실패했습니다.";
            setToast({ open: true, message: msg, severity: "error" });
        }
    }, []);

    const saveBackground = async () => {
        try {
            await api.patch("/api/miniroom/room/", {
                background: bgId,     // 숫자 ID
                width: room.width,
                height: room.height,
            });
            setToast({ open: true, message: "배경 저장 완료", severity: "success" });
            const { data } = await api.get("/api/miniroom/room/");
            setRoom(data);
            setBgId(data.background?.id ?? null);

        } catch {
            setToast({ open: true, message: "배경 저장 실패", severity: "error" });
        }
    };

    // ✅ 배경 제거 
    const clearBackground = async () => {
        try {
            // 로컬 상태 먼저 해제 
            setBgId(null);
            setRoom(prev => (prev ? { ...prev, background: null } : prev));

            // 서버 반영 
            await api.patch("/api/miniroom/room/", {
                background: null,
                width: room.width,
                height: room.height,
            });

            setToast({ open: true, message: "배경 제거 완료", severity: "success" });

            // 서버 상태 새로고침(선택) 
            const { data } = await api.get("/api/miniroom/room/");
            setRoom(data);
            setBgId(data.background?.id ?? null);
        } catch {
            setToast({ open: true, message: "배경 제거 실패", severity: "error" });
        }
    };

    const savePlacements = async () => {
        try {
            const normalized = normalizeZHints(placements);
            setPlacements(normalized); // 로컬도 업데이트

            await api.post("/api/miniroom/placements/bulk/", {
                placements: normalized.map(p => ({
                    furniture_id: p.furniture.id,
                    x: p.x, y: p.y,
                    rotation: p.rotation || 0,
                    z_hint: p.z_hint || 0,
                    mirror: !!p.mirror,
                    direction: p.direction || "front",
                    width: p.width || null,
                    height: p.height || null,
                }))
            });
            setToast({ open: true, message: "배치 저장 완료", severity: "success" });
            const { data } = await api.get("/api/miniroom/room/");
            setPlacements(normalizeZHints(data.placements || []));
        } catch (e) {
            const msg = e?.response?.data?.detail || "배치 저장 실패";
            setToast({ open: true, message: String(msg), severity: "error" });
        }
    };


    // ---- 디바운스 저장 (드래그 멈춘 뒤 400ms 후 일괄 저장) ---- 
    const savePlacementsDebouncedRef = useRef(null);
    const normalizeZHints = useCallback((list) => {
        // 안전 복사 + 원래 인덱스 보존
        const arr = list.map((p, i) => ({ ...p, __i: i }));
        // z_hint(오름차순) → y → x 순 정렬 (렌더 정렬과 동일 기준)
        arr.sort((a, b) =>
            (a.z_hint || 0) - (b.z_hint || 0) ||
            (a.y - b.y) ||
            (a.x - b.x)
        );
        // 0부터 순차 재배정
        arr.forEach((p, rank) => { p.z_hint = rank; });
        // 원래 자리로 되돌리기
        const out = list.map((p) => ({ ...p }));
        arr.forEach((p) => { out[p.__i] = p; });
        return out;
    }, []);

    const scheduleAutosave = useCallback((nextPlacements) => {
        if (savePlacementsDebouncedRef.current) {
            clearTimeout(savePlacementsDebouncedRef.current);
        }
        const normalized = normalizeZHints(nextPlacements); // ← 정규화 선반영
        savePlacementsDebouncedRef.current = setTimeout(async () => {
            try {
                await api.post("/api/miniroom/placements/bulk/", {
                    placements: normalized.map(p => ({
                        furniture_id: p.furniture.id,
                        x: p.x, y: p.y,
                        rotation: p.rotation || 0,
                        z_hint: p.z_hint || 0,
                        mirror: !!p.mirror,
                        direction: p.direction || "front",
                        width: p.width || null,
                        height: p.height || null,
                    }))
                });
                setToast({ open: true, message: "자동 저장 완료", severity: "success" });
            } catch (e) {
                const msg = e?.response?.data?.detail || "자동 저장 실패";
                setToast({ open: true, message: String(msg), severity: "error" });
            }
        }, 400);
    }, [normalizeZHints]); // ★ 여기! normalizeZHints 추가
    useEffect(() => {
        return () => {
            if (savePlacementsDebouncedRef.current) {
                clearTimeout(savePlacementsDebouncedRef.current);
            }
        };
    }, []);

    // ---------- 유틸 ----------
    // z_hint 정규화: 현재 정렬(작을수록 뒤) 보존하며 0,1,2,...로 재배치

    const widthPx = (room?.width || 0) * GRID;
    const heightPx = (room?.height || 0) * GRID;

    const snap = (v) => Math.max(0, Math.round(v / GRID));
    const clampToRoom = useCallback((x, y, fw = 1, fh = 1) => {
        if (!room) return { x: 0, y: 0 };
        const maxX = Math.max(0, room.width - fw);
        const maxY = Math.max(0, room.height - fh);
        return { x: Math.min(maxX, Math.max(0, x)), y: Math.min(maxY, Math.max(0, y)) };
    }, [room]);

    const sorted = useMemo(() => {
        // z_hint(큰 값이 위) → y → x 순으로 정렬 
        return [...placements]
            .map((p, i) => ({ ...p, __i: i }))
            .sort((a, b) => (a.z_hint || 0) - (b.z_hint || 0) || (a.y - b.y) || (a.x - b.x));
    }, [placements]);

    // ---------- 카탈로그(왼쪽) → 캔버스 드래그앤드롭 ----------
    // HTML5 DnD로 신규 배치
    const onCatalogDragStart = (e, furnitureId) => {
        e.dataTransfer.setData("text/furniture_id", String(furnitureId));
        e.dataTransfer.effectAllowed = "copy";
    };
    const onCanvasDragOver = (e) => {
        if (editingDisabled) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copyMove";
    };
    const onCanvasDrop = (e) => {
        e.preventDefault();
        if (editingDisabled) return;
        const fid = Number(e.dataTransfer.getData("text/furniture_id"));
        if (!fid) return;
        const furn = ownedFurns.find(f => f.id === fid);
        if (!furn) return;

        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX;
        const clientY = e.clientY;
        const localX = clientX - rect.left;
        const localY = clientY - rect.top;

        const gx = snap(localX);
        const gy = snap(localY);
        const { x, y } = clampToRoom(gx, gy, furn.size_w, furn.size_h);
        setPlacements(prev => {
            const next = [...prev, {
                furniture: furn, x, y,
                rotation: 0, z_hint: 0, direction: "front",
                width: furn.size_w, height: furn.size_h
            }];
            setSelectedIdx(next.length - 1);
            scheduleAutosave(next); // (선택) 드롭 즉시 자동 저장 
            return next;
        });
    };


    // ---------- 키보드 조작(회전/삭제/미세이동) ----------
    useEffect(() => {
        const onKey = (e) => {
            if (editingDisabled) return;
            if (selectedIdx == null) return;
            // 캔버스에 포커스가 있는지 정도는 간단히 체크(없어도 동작하길 원하면 제거)
            if (!canvasRef.current) return;

            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                setPlacements(prev => {
                    const next = prev.filter((_, i) => i !== selectedIdx);
                    scheduleAutosave(next);
                    return next;
                });
                setSelectedIdx(null);
            }
            if (e.key.toLowerCase() === "r") {
                e.preventDefault();
                setPlacements(prev => {
                    const next = [...prev];
                    const p = { ...next[selectedIdx] };
                    if (!p) return prev;
                    // rotatable이 false면 무시
                    if (p.furniture?.rotatable === false) return prev;
                    p.rotation = ((p.rotation || 0) + ROTATION_STEP) % 360;
                    if (p.width && p.height && (ROTATION_STEP % 180 === 90)) {
                        const tmp = p.width; p.width = p.height; p.height = tmp;
                        const clamped = clampToRoom(p.x, p.y, p.width, p.height);
                        p.x = clamped.x; p.y = clamped.y;
                    }
                    next[selectedIdx] = p;
                    scheduleAutosave(next);
                    return next;
                });
            }

            if (e.key.toLowerCase() === "b") {
                e.preventDefault();
                setPlacements(prev => {
                    const next = [...prev];
                    const p = { ...next[selectedIdx] };
                    p.direction = (p.direction === "back" ? "front" : "back");
                    next[selectedIdx] = p;
                    scheduleAutosave(next);
                    return next;
                });
            }

            if (e.key.toLowerCase() === "f") {
                e.preventDefault();
                setPlacements(prev => {
                    const next = [...prev];
                    const p = { ...next[selectedIdx] };
                    if (!p) return prev;
                    p.mirror = !p.mirror;
                    next[selectedIdx] = p;
                    scheduleAutosave(next);
                    return next;
                });
            }
            // 화살표로 1칸 이동
            const delta = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
            if (delta) {
                e.preventDefault();
                setPlacements(prev => {
                    const next = [...prev];
                    const p = { ...next[selectedIdx] };
                    if (!p) return prev;
                    const furnW = p.width || p.furniture.size_w || 1;   // ▼ 리사이즈 반영
                    const furnH = p.height || p.furniture.size_h || 1;  // ▼ 리사이즈 반영
                    const { x, y } = clampToRoom(p.x + delta[0], p.y + delta[1], furnW, furnH);
                    p.x = x; p.y = y;
                    next[selectedIdx] = p;
                    scheduleAutosave(next);
                    return next;
                });
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedIdx, room, clampToRoom, scheduleAutosave, editingDisabled]);


    if (loading || !room) {
        return (
            <Box p={3} display="flex" alignItems="center" justifyContent="center" minHeight={320}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box p={2} sx={{ overflow: "hidden" }}>
            <Typography variant="h5" fontWeight={700} mb={1}>미니룸 편집</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                팁: 카탈로그에서 드래그해 놓기, 캔버스에서 드래그로 이동, <b>R</b> 회전, <b>B</b> 앞/뒤 전환, <b>F</b> 좌우 반전, <b>Delete</b> 삭제, 화살표키로 미세 이동
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                {/* 좌측: 카탈로그 */}
                <Paper elevation={0} sx={{ p: 0, width: { xs: "100%", md: 300 }, border: "1px solid #eee", borderRadius: 2, flexShrink: 0 }}>
                    {/* 탭바 */}
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700}>카탈로그</Typography>
                    </Box>

                    {/* 1) 상단: 소유/상점 */}
                    <Tabs
                        value={catalogMode}
                        onChange={(_, v) => setCatalogMode(v)}
                        variant="fullWidth"
                        sx={{ borderBottom: "1px solid #eee" }}
                    >
                        <Tab label="소유 카탈로그" value="owned" />
                        <Tab label="상점 카탈로그" value="shop" />
                    </Tabs>

                    {/* 2) 하단: 오브젝트/배경 */}
                    <Tabs
                        value={itemTab}
                        onChange={(_, v) => setItemTab(v)}
                        variant="fullWidth"
                        sx={{ borderBottom: "1px solid #eee" }}
                    >
                        <Tab label="오브젝트" value="furniture" />
                        <Tab label="배경" value="background" />
                    </Tabs>

                    {/* 3) 콘텐츠 */}
                    <Box sx={{ p: 2 }}>
                        {/* 소유 + 오브젝트 */}
                        {catalogMode === "owned" && itemTab === "furniture" && (
                            <>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="body2" color="text.secondary">내가 소유한 가구</Typography>
                                    <Button size="small" variant="text" onClick={loadAll}>새로고침</Button>
                                </Stack>
                                <List dense sx={{ maxHeight: 520, overflow: "auto" }}>
                                    {ownedFurns.map((f) => (
                                        <ListItem
                                            key={f.id}
                                            draggable
                                            onDragStart={(e) => onCatalogDragStart(e, f.id)}
                                            secondaryAction={
                                                <Tooltip title="캔버스 중앙에 추가">
                                                    <IconButton
                                                        disabled={editingDisabled}
                                                        size="small"
                                                        onClick={() => {
                                                            if (editingDisabled) return;
                                                            const cx = Math.floor(room.width / 2);
                                                            const cy = Math.floor(room.height / 2);
                                                            const { x, y } = clampToRoom(cx, cy, f.size_w, f.size_h);
                                                            setPlacements(prev => {
                                                                const next = [...prev, {
                                                                    furniture: f, x, y,
                                                                    rotation: 0, z_hint: 0, direction: "front",
                                                                    width: f.size_w, height: f.size_h
                                                                }];
                                                                setSelectedIdx(next.length - 1);
                                                                scheduleAutosave(next);
                                                                return next;
                                                            });
                                                        }}
                                                    >
                                                        <SaveIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar variant="square" src={f.sprite_url} imgProps={{ style: { imageRendering: "pixelated" } }} />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={f.name}
                                                secondary={`${f.size_w || 1}×${f.size_h || 1}${f.rotatable === false ? " (회전불가)" : ""}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}

                        {/* 소유 + 배경 */}
                        {catalogMode === "owned" && itemTab === "background" && (
                            <>
                                <Typography variant="body2" color="text.secondary" mb={1}>내가 소유한 배경</Typography>
                                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                    {/* ✅ ‘배경 없음’ 타일 */}
                                    <Box
                                        key="bg-none"
                                        onClick={() => { setRoom(prev => (prev ? { ...prev, background: null } : prev)); setBgId(null); }}
                                        sx={{
                                            width: 96, height: 72, borderRadius: 1, cursor: "pointer",
                                            border: bgId == null ? "2px solid #1976d2" : "1px solid #ddd",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            backgroundColor: "#fafafa",
                                        }}
                                        title="배경 없음"
                                    >
                                        <Typography variant="caption" color="text.secondary">배경 없음</Typography>
                                    </Box>

                                    {ownedBgs.map(b => (
                                        <Box
                                            key={b.id}
                                            onClick={() => { setRoom(prev => prev ? { ...prev, background: b } : prev); setBgId(b.id); }}
                                            sx={{
                                                width: 96, height: 72, borderRadius: 1, cursor: "pointer",
                                                border: bgId === b.id ? "2px solid #1976d2" : "1px solid #ddd",
                                                backgroundImage: b.image_url ? `url(${b.image_url})` : "none",
                                                backgroundSize: "cover", backgroundPosition: "center",
                                            }}
                                            title={b.name}
                                        />
                                    ))}
                                </Stack>
                                <Stack direction="row" spacing={1} mt={1} alignItems="center">
                                    <Button size="small" variant="contained" onClick={saveBackground} disabled={editingDisabled}>
                                        배경 저장
                                    </Button>
                                    {/* ✅ 배경 제거 버튼 */}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={clearBackground}
                                        disabled={editingDisabled || !room?.background}
                                    >
                                        배경 제거
                                    </Button>
                                    <Button size="small" variant="text" onClick={loadAll}>새로고침</Button>
                                </Stack>
                            </>
                        )}

                        {/* 상점 + 오브젝트 */}
                        {catalogMode === "shop" && itemTab === "furniture" && (
                            <List dense sx={{ maxHeight: 520, overflow: "auto" }}>
                                {shopFurns.map((f) => {
                                    const isOwned = ownedFurnIds.has(f.id);
                                    const isSoldOut = (f.is_unlimited === false) && (f.stock <= 0);
                                    const btnLabel = isOwned ? "구매완료" : isSoldOut ? "품절" : `${f.price ?? "?"} Leaf 구매`;
                                    return (
                                        <ListItem key={`shop-f-${f.id}`}
                                            secondaryAction={
                                                <Button
                                                    size="small"
                                                    variant={isOwned ? "outlined" : "contained"}
                                                    disabled={isOwned || isSoldOut || purchaseLoading}
                                                    onClick={() => !isOwned && !isSoldOut && buyFromShop("furniture", f.id)}
                                                >
                                                    {btnLabel}
                                                </Button>
                                            }
                                        >
                                            <ListItemAvatar>
                                                <Avatar variant="square" src={f.sprite_url} imgProps={{ style: { imageRendering: "pixelated" } }} />
                                            </ListItemAvatar>
                                            <ListItemText primary={f.name} secondary={`${f.size_w || 1}×${f.size_h || 1}`} />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}

                        {/* 상점 + 배경 */}
                        {catalogMode === "shop" && itemTab === "background" && (
                            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                                {shopBgs.map((b) => {
                                    const isOwned = ownedBgIds.has(b.id);
                                    const isSoldOut = (b.is_unlimited === false) && (b.stock <= 0);
                                    const btnLabel = isOwned ? "구매완료" : isSoldOut ? "품절" : `${b.price ?? "?"} Leaf`;
                                    return (
                                        <Paper key={`shop-b-${b.id}`} sx={{ p: 1, width: 120, borderRadius: 1, textAlign: "center" }}>
                                            <Box sx={{
                                                width: "100%", height: 72, mb: 1,
                                                backgroundImage: b.image_url ? `url(${b.image_url})` : "none",
                                                backgroundSize: "cover", backgroundPosition: "center", border: "1px solid #eee", borderRadius: 1
                                            }} />
                                            <Typography variant="caption" display="block" noWrap title={b.name} sx={{ mb: 0.5 }}>{b.name}</Typography>
                                            <Button
                                                size="small"
                                                fullWidth
                                                variant={isOwned ? "outlined" : "contained"}
                                                disabled={isOwned || isSoldOut || purchaseLoading}
                                                onClick={() => !isOwned && !isSoldOut && buyFromShop("background", b.id)}
                                            >
                                                {btnLabel}
                                            </Button>
                                        </Paper>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                </Paper>

                {/* 우측: 에디터 */}
                <Box sx={{ flex: 1, minWidth: 320 }}>



                    {/* 툴바 */}
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                        <Tooltip title="회전 (R)">
                            <span>
                                <IconButton
                                    disabled={editingDisabled || selectedIdx == null || placements[selectedIdx]?.furniture?.rotatable === false}
                                    onClick={() => {
                                        if (selectedIdx == null) return;
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const p = { ...next[selectedIdx] };
                                            p.rotation = ((p.rotation || 0) + ROTATION_STEP) % 360;
                                            // ▼ 커스텀 크기 사용 중이면 90/270 회전 시 W/H 스왑 + 룸 경계 클램프
                                            if (p.width && p.height && (ROTATION_STEP % 180 === 90)) {
                                                const tmp = p.width; p.width = p.height; p.height = tmp;
                                                const clamped = clampToRoom(p.x, p.y, p.width, p.height);
                                                p.x = clamped.x; p.y = clamped.y;
                                            }
                                            next[selectedIdx] = p;
                                            scheduleAutosave(next);
                                            return next;
                                        });
                                    }}
                                >
                                    <RotateRightIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="앞/뒤 전환 (B)">
                            <span>
                                <IconButton
                                    disabled={editingDisabled || selectedIdx == null}
                                    onClick={() => {
                                        if (selectedIdx == null) return;
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const p = { ...next[selectedIdx] };
                                            p.direction = (p.direction === "back" ? "front" : "back");
                                            next[selectedIdx] = p;
                                            scheduleAutosave(next);
                                            return next;
                                        });
                                    }}
                                >
                                    <FlipCameraAndroidIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="좌우 반전 (F)">
                            <span>
                                <IconButton
                                    disabled={editingDisabled || selectedIdx == null}
                                    onClick={() => {
                                        if (selectedIdx == null) return;
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const p = { ...next[selectedIdx] };
                                            p.mirror = !p.mirror;
                                            next[selectedIdx] = p;
                                            scheduleAutosave(next);
                                            return next;
                                        });
                                    }}
                                >
                                    <SwapHorizIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="맨앞으로">
                            <span>
                                <IconButton
                                    disabled={editingDisabled || selectedIdx == null}
                                    onClick={() => {
                                        if (selectedIdx == null) return;
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const maxZh = next.reduce((m, it) => Math.max(m, it.z_hint || 0), 0);
                                            next[selectedIdx] = { ...next[selectedIdx], z_hint: maxZh + 1 };
                                            const normalized = normalizeZHints(next);   // ★ 정규화
                                            scheduleAutosave(normalized);
                                            return normalized;
                                        });
                                    }}
                                >
                                    <FlipToFrontIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="맨뒤로">
                            <span>
                                <IconButton
                                    disabled={editingDisabled || selectedIdx == null}
                                    onClick={() => {
                                        if (selectedIdx == null) return;
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const minZh = next.reduce((m, it) => Math.min(m, it.z_hint || 0), 0);
                                            next[selectedIdx] = { ...next[selectedIdx], z_hint: minZh - 1 };
                                            const normalized = normalizeZHints(next);
                                            scheduleAutosave(normalized);
                                            return normalized;
                                        });
                                    }}
                                >
                                    <FlipToBackIcon />
                                </IconButton>
                            </span>
                        </Tooltip>



                        <Tooltip title="삭제 (Delete)">
                            <span>
                                <IconButton
                                    disabled={editingDisabled || selectedIdx == null}
                                    onClick={() => {
                                        if (selectedIdx == null) return;
                                        setPlacements(prev => {
                                            const next = prev.filter((_, i) => i !== selectedIdx);
                                            scheduleAutosave(next);
                                            return next;
                                        });
                                        setSelectedIdx(null);
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={savePlacements} disabled={editingDisabled}>
                            배치 저장
                        </Button>
                        <Button
                            variant="text"
                            startIcon={<ReplayIcon />}
                            onClick={loadAll}
                        >
                            되돌리기(서버상태)
                        </Button>
                        {/* 툴바 끝쪽에 추가 */}
                        <Stack direction="row" spacing={1} alignItems="center" ml="auto">
                            <TextField
                                size="small"
                                label="가로(W)"
                                type="number"
                                value={room.width}
                                onChange={(e) => setRoom(r => ({ ...r, width: Math.max(4, Number(e.target.value || 0)) }))}
                                sx={{ width: 110 }}
                                inputProps={{ min: 4 }}
                            />
                            <TextField
                                size="small"
                                label="세로(H)"
                                type="number"
                                value={room.height}
                                onChange={(e) => setRoom(r => ({ ...r, height: Math.max(4, Number(e.target.value || 0)) }))}
                                sx={{ width: 110 }}
                                inputProps={{ min: 4 }}
                            />
                            <Button
                                variant="outlined"
                                disabled={editingDisabled}
                                onClick={async () => {
                                    try {
                                        await api.patch("/api/miniroom/room/", {
                                            background: bgId,
                                            width: room.width,
                                            height: room.height,
                                        });
                                        setToast({ open: true, message: "방 크기 저장 완료", severity: "success" });
                                        const { data } = await api.get("/api/miniroom/room/");
                                        setRoom(data);
                                    } catch {
                                        setToast({ open: true, message: "방 크기 저장 실패", severity: "error" });
                                    }
                                }}
                            >
                                크기 저장
                            </Button>
                        </Stack>
                    </Stack>

                    {/* 캔버스 */}
                    <Box

                        ref={canvasRef}
                        onDragOver={onCanvasDragOver}
                        onDrop={onCanvasDrop}
                        onMouseDown={(e) => {
                            canvasRef.current?.focus();
                            if (e.target === canvasRef.current) setSelectedIdx(null);
                        }}
                        tabIndex={0}
                        sx={{
                            position: "relative",
                            width: widthPx,
                            height: heightPx,
                            overflow: "hidden",
                            imageRendering: "pixelated",
                            borderRadius: 2,
                            border: "1px solid rgba(0,0,0,0.1)",
                            backgroundColor: "#eaeaea",
                            outline: "none",
                        }}
                    >
                        {/* ✅ 완성 배경 1장 */}
                        {/* ✅ 완성 배경 1장 */}
                        {bg?.image_url && (
                            <img
                                src={bg.image_url}
                                alt="room background"
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    top: "50%",
                                    width: "100%",
                                    height: "100%",
                                    objectFit:
                                        bg.fit_mode === "cover" ? "cover" :
                                            bg.fit_mode === "contain" ? "contain" : "fill",
                                    transform: `translate(calc(-50% + ${bg.offset_x || 0}px),
                            calc(-50% + ${bg.offset_y || 0}px))
                  scale(${bg.scale || 1})`,
                                    transformOrigin: "center center",
                                    pointerEvents: "none",
                                    userSelect: "none",
                                }}
                                draggable={false}
                            />
                        )}



                        {/* 배치 이미지들 (깊이 정렬) */}
                        {sorted.map((p) => {
                            const isActive = p.__i === selectedIdx;
                            const w = (p.width || p.furniture.size_w || 1) * GRID;
                            const h = (p.height || p.furniture.size_h || 1) * GRID;
                            const imgSrc =
                                (p.direction === "back" && p.furniture?.back_sprite_url)
                                    ? p.furniture.back_sprite_url
                                    : p.furniture.sprite_url;
                            return (
                                <Rnd
                                    key={`p-${p.__i}`}
                                    bounds="parent"
                                    size={{ width: w, height: h }}
                                    position={{ x: p.x * GRID, y: p.y * GRID }}
                                    dragGrid={[GRID, GRID]}
                                    resizeGrid={[GRID, GRID]}
                                    disableDragging={editingDisabled}
                                    enableResizing={editingDisabled ? false : {
                                        top: true, right: true, bottom: true, left: false,
                                        topRight: true, bottomRight: true, bottomLeft: true, topLeft: true,
                                    }}
                                    minWidth={GRID}
                                    minHeight={GRID}

                                    /* ✅ 선택 시 테두리/그림자 강조 */
                                    style={{
                                        zIndex: 1000 + (p.z_hint || 0), // z_hint가 클수록 위
                                        outline: isActive ? "2px solid #1976d2" : "1px solid transparent",
                                        borderRadius: 4,
                                        boxShadow: isActive ? "0 0 0 2px rgba(25,118,210,0.20)" : "none",
                                        transition: "outline 120ms, box-shadow 120ms",
                                    }}

                                    /* ✅ 리사이즈 핸들 강조(선택 시만 보이게) */
                                    resizeHandleStyles={{
                                        bottomRight: {
                                            width: HANDLE,
                                            height: HANDLE,
                                            right: -HANDLE / 2,
                                            bottom: -HANDLE / 2,
                                            borderRadius: 6,
                                            background: isActive ? "#1976d2" : "transparent",
                                            boxShadow: isActive ? "0 0 0 2px rgba(25,118,210,0.25)" : "none",
                                            cursor: "nwse-resize",
                                        },
                                        right: {
                                            width: HANDLE,
                                            right: -HANDLE / 2,
                                            top: "calc(50% - 12px)",
                                            height: 24,
                                            borderRadius: 4,
                                            background: isActive ? "rgba(25,118,210,0.85)" : "transparent",
                                            cursor: "e-resize",
                                        },
                                        bottom: {
                                            height: HANDLE,
                                            bottom: -HANDLE / 2,
                                            left: "calc(50% - 12px)",
                                            width: 24,
                                            borderRadius: 4,
                                            background: isActive ? "rgba(25,118,210,0.85)" : "transparent",
                                            cursor: "s-resize",
                                        },
                                        /* 코너 핸들도 쓰고 싶으면 아래도 열어두세요
                                        bottomLeft: {...},
                                        topRight: {...},
                                        topLeft: {...},
                                        */
                                    }}
                                    onMouseDown={() => setSelectedIdx(p.__i)}
                                    onDragStop={(e, data) => {
                                        const gridX = Math.max(0, Math.round(data.x / GRID));
                                        const gridY = Math.max(0, Math.round(data.y / GRID));
                                        const curW = p.width || p.furniture.size_w || 1;
                                        const curH = p.height || p.furniture.size_h || 1;
                                        const { x: clampedX, y: clampedY } = clampToRoom(gridX, gridY, curW, curH);
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const originalIndex = p.__i;
                                            const item = { ...next[originalIndex] };
                                            item.x = clampedX;
                                            item.y = clampedY;
                                            next[originalIndex] = item;
                                            scheduleAutosave(next);
                                            return next;
                                        });
                                    }}
                                    onResizeStop={(e, direction, ref, delta, position) => {
                                        const newW = Math.round(ref.offsetWidth / GRID);
                                        const newH = Math.round(ref.offsetHeight / GRID);
                                        const gridX = Math.round(position.x / GRID);
                                        const gridY = Math.round(position.y / GRID);
                                        const { x: clampedX, y: clampedY } = clampToRoom(gridX, gridY, newW, newH);
                                        setPlacements(prev => {
                                            const next = [...prev];
                                            const originalIndex = p.__i;
                                            const item = { ...next[originalIndex] };
                                            item.width = newW;
                                            item.height = newH;
                                            item.x = clampedX;
                                            item.y = clampedY;
                                            next[originalIndex] = item;
                                            scheduleAutosave(next);
                                            return next;
                                        });
                                    }}
                                >
                                    <img
                                        src={imgSrc}
                                        alt={p.furniture.name}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            transform: `${p.mirror ? "scaleX(-1) " : ""}rotate(${p.rotation || 0}deg)`,
                                            imageRendering: "pixelated",
                                            userSelect: "none",
                                            pointerEvents: "none"
                                        }}
                                        draggable={false}
                                    />
                                </Rnd>
                            );
                        })}
                        {/* 격자 배경(옵션) */}
                        <GridOverlay widthPx={widthPx} heightPx={heightPx} />
                        {/* Paywall Overlay: 권한 없을 때 화면 덮기 */}
                        {editingDisabled && (
                            <Box sx={{
                                position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.9)",
                                display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000
                            }}>
                                <Paper sx={{ p: 3, borderRadius: 2, textAlign: "center", minWidth: 320 }}>
                                    <Typography variant="h6" fontWeight={700} mb={1}>프리미엄 미니룸</Typography>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        구매 후 편집 기능이 활성화됩니다.
                                    </Typography>
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                        <Button variant="contained" onClick={startPurchase} disabled={purchaseLoading}>
                                            {purchaseLoading ? "구매 진행중..." : "구매하기"}
                                        </Button>
                                        <Button variant="text" onClick={fetchEntitlement}>다시 확인</Button>
                                    </Stack>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Stack>

            <Snackbar
                open={toast.open}
                autoHideDuration={2500}
                onClose={() => setToast(t => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

/** 격자 오버레이 (가벼운 SVG) */
function GridOverlay({ widthPx, heightPx }) {
    const patternId = "miniroom-grid-pattern";
    return (
        <svg width={widthPx} height={heightPx} style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", zIndex: 5 }}>
            <defs>
                <pattern id={patternId} width={GRID} height={GRID} patternUnits="userSpaceOnUse">
                    <path d={`M ${GRID} 0 L 0 0 0 ${GRID}`} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="1" />
                </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#${patternId})`} />
        </svg>
    );
}

